import type { Vec2 } from '../../geo/types';
import type { BakedCrowd } from './crowd/crowdPlan';
import { PLACE_KINDS } from './crowd/crowdPlaces';
import { restoreWalkNet, type Ground } from './crowd/walkNet';
import { routeTree } from './crowd/routes';
import { alongLine, lineOf, type OnLine, type WalkLine } from './crowd/walkLine';

export const CARGO = {
  count: 24,
  speed: 0.85,
  wheelRadius: 0.42,
  shaftHalf: 0.351,
  gripX: 0.436,
  axleX: 1.235,
  lengths: [0.82, 1, 1.28],
} as const;

export function cargoX(x: number, length: number): number {
  return CARGO.gripX + (x - CARGO.gripX) * length;
}

/** Parallel delivery lanes with round ends, so workers turn their carts instead of reversing. */
export function cargoDeliveryLoop(path: readonly Vec2[], radius = 0.7): Vec2[] {
  const directions = path.map((_, i) => {
    const before = path[Math.max(0, i - 1)]!,
      after = path[Math.min(path.length - 1, i + 1)]!;
    const angle = Math.atan2(after[1] - before[1], after[0] - before[0]);
    return [Math.cos(angle), Math.sin(angle)] as Vec2;
  });
  const lane = (i: number, side: number): Vec2 => {
    const [x, y] = path[i]!,
      [dx, dy] = directions[i]!;
    return [x - dy * radius * side, y + dx * radius * side];
  };
  const points = path.map((_, i) => lane(i, 1));
  const end = (i: number, sign: number) => {
    const [x, y] = path[i]!,
      [dx, dy] = directions[i]!;
    for (let k = 1; k <= 12; k++) {
      const a = (k * Math.PI) / 12;
      points.push([
        x + sign * radius * (dx * Math.sin(a) - dy * Math.cos(a)),
        y + sign * radius * (dy * Math.sin(a) + dx * Math.cos(a)),
      ]);
    }
  };
  end(path.length - 1, 1);
  for (let i = path.length - 2; i >= 0; i--) points.push(lane(i, -1));
  end(0, -1);
  points.pop(); // The closing cap already ends at the first point.
  // Round the remaining street corners with short quadratic arcs.
  const route: Vec2[] = [];
  points.forEach((p, i) => {
    const a = points[(i + points.length - 1) % points.length]!,
      b = points[(i + 1) % points.length]!;
    const da = Math.hypot(a[0] - p[0], a[1] - p[1]),
      db = Math.hypot(b[0] - p[0], b[1] - p[1]);
    const reach = Math.min(0.7, da * 0.35, db * 0.35);
    const enter: Vec2 = [p[0] + ((a[0] - p[0]) * reach) / da, p[1] + ((a[1] - p[1]) * reach) / da];
    const leave: Vec2 = [p[0] + ((b[0] - p[0]) * reach) / db, p[1] + ((b[1] - p[1]) * reach) / db];
    for (let k = 0; k <= 4; k++) {
      const t = k / 4,
        u = 1 - t;
      route.push([
        u * u * enter[0] + 2 * u * t * p[0] + t * t * leave[0],
        u * u * enter[1] + 2 * u * t * p[1] + t * t * leave[1],
      ]);
    }
  });
  route.push(route[0]!);
  return route;
}

/** Layout points covered by the worker, shafts, wheels, bed and longest timber load. */
export function cargoFootprint(at: Vec2, heading: number, length = 1): Vec2[] {
  const c = Math.cos(heading),
    s = Math.sin(heading);
  const axle = cargoX(CARGO.axleX, length);
  const front = Math.max(cargoX(1.75, length), 1.94 + axle - CARGO.axleX);
  return [-0.2, 0.45, axle, front].flatMap((x) =>
    [-0.56, 0, 0.56].map((y) => [at[0] + c * x - s * y, at[1] + s * x + c * y] as Vec2)
  );
}

export function cargoHeadingAt(line: WalkLine, distance: number): number {
  const before: OnLine = { x: 0, y: 0, ground: 0, heading: 0 },
    after = { ...before };
  const wrap = (d: number) => (((d % line.length) + line.length) % line.length) / line.length;
  alongLine(line, wrap(distance - 0.6), before);
  alongLine(line, wrap(distance + 0.6), after);
  return Math.hypot(after.x - before.x, after.y - before.y) > 0.01
    ? Math.atan2(after.y - before.y, after.x - before.x)
    : after.heading;
}

/** Cargo stays on broad connected ways, and its complete footprint must clear buildings/water. */
export interface CargoRouting {
  readonly count: number;
  readonly clearanceM: number;
  readonly turnRadiusM: number;
  readonly footprint: (line: WalkLine, distance: number) => readonly Vec2[];
}

const HANDCART_ROUTING: CargoRouting = {
  count: CARGO.count,
  clearanceM: 1.3,
  turnRadiusM: 0.7,
  footprint(line, distance) {
    const at: OnLine = { x: 0, y: 0, ground: 0, heading: 0 };
    alongLine(line, distance / line.length, at);
    return cargoFootprint([at.x, at.y], cargoHeadingAt(line, distance), Math.max(...CARGO.lengths));
  },
};

export function planCargoRoutes(
  crowd: BakedCrowd,
  ground: Ground,
  routing: CargoRouting = HANDCART_ROUTING
): readonly (readonly Vec2[])[] {
  const original = restoreWalkNet(crowd.net);
  const edges: number[] = [];
  for (let e = 0; e < crowd.net.edges.length; e += 2) {
    const a = crowd.net.edges[e]!,
      b = crowd.net.edges[e + 1]!;
    if (original.half[a]! < routing.clearanceM || original.half[b]! < routing.clearanceM) continue;
    edges.push(a, b);
  }
  const net = restoreWalkNet({ nodes: crowd.net.nodes, edges });
  const starts: number[] = [];
  for (let p = 0; p < crowd.places.length; p += 3) {
    const kind = PLACE_KINDS[crowd.places[p]!]!,
      node = crowd.places[p + 1]!;
    if (
      (kind === 'load' || kind === 'market' || kind === 'quay') &&
      net.half[node]! >= routing.clearanceM &&
      net.start[node + 1]! > net.start[node]!
    )
      starts.push(node);
  }
  const remaining = new Set(starts);
  const centers: Vec2[] = [];
  const routes: Vec2[][] = [];
  while (remaining.size && routes.length < routing.count) {
    let a = -1,
      score = -Infinity;
    for (const node of remaining) {
      const x = net.x[node]!,
        y = net.y[node]!;
      const separation = centers.length
        ? Math.min(...centers.map(([cx, cy]) => (x - cx) ** 2 + (y - cy) ** 2))
        : x * x + y * y;
      if (separation > score) {
        a = node;
        score = separation;
      }
    }
    remaining.delete(a);
    const tree = routeTree(net, a);
    const ends = [
      ...new Set(starts.filter((node) => tree.distance[node]! > 55 && tree.distance[node]! < 220)),
    ];
    for (let attempt = 0; attempt < Math.min(ends.length, 16); attempt++) {
      const b = ends[(a * 17 + attempt * 13) % ends.length]!;
      const path: number[] = [];
      for (let node = b; node >= 0; node = tree.next[node]!) path.push(node);
      if (path.length < 4) continue;
      const route = cargoDeliveryLoop(
        path.map((i): Vec2 => [net.x[i]!, net.y[i]!]),
        routing.turnRadiusM
      );
      const line = lineOf(route.map(([x, y]) => [x, y, 0]));
      if (!Number.isFinite(line.length)) continue;
      let safe = true;
      for (let d = 0; d < line.length; d += 0.5) {
        if (!routing.footprint(line, d).every(ground.open)) {
          safe = false;
          break;
        }
      }
      if (!safe) continue;
      routes.push(route);
      const center = path.reduce(
        (sum: Vec2, i): Vec2 => [
          sum[0] + net.x[i]! / path.length,
          sum[1] + net.y[i]! / path.length,
        ],
        [0, 0]
      );
      centers.push(center);
      remaining.delete(b);
      break;
    }
  }
  return routes;
}
